// Where everything is served from, and what day it is.
//
// Two of these are worth reading twice.
//
// `routeGraphUrl` is load-bearing here in a way it is not in the customer
// app. A dispatch ticket is bound to the graph's epoch (§20.6) and a
// round is ordered by `engine.itinerary()`, so a console with no graph
// can show you what you have already sent and cannot mint anything new.
// That is the honest state, not an error, and `?nomap=1` forces it on a
// machine that does have a graph — a state you cannot reach is a state
// nobody checks.
//
// `?today=` moves this console's clock. The ladder is a function of
// history over days — the same round on three days inside a fortnight,
// a rung unused for sixty — and none of that is reachable in one sitting
// otherwise. It shifts by whole days, it is loud about being on, and
// everything it writes is a real record with a real timestamp.

// There is no `location` in a test runner or any other non-browser host, and
// this module is reached from `ops/dispatch.ts` — the part worth testing in
// plain Node. Reading the URL is how the console is configured, not what it
// is, so its absence is a default, never a crash.
const browserLocation: { search: string; origin: string } =
  typeof location === "undefined"
    ? { search: "", origin: "http://localhost/" }
    : location;

const params = new URLSearchParams(browserLocation.search);

/** The dev server's local graph, or "" in any production build. */
const devRouteGraph = typeof __DEV_ROUTE_GRAPH__ === "string" ? __DEV_ROUTE_GRAPH__ : "";
const devTrackBase = typeof __DEV_TRACK_BASE__ === "string" ? __DEV_TRACK_BASE__ : "";

export const config = {
  engineBaseUrl: import.meta.env.VITE_ENGINE_BASE || "/rangefind/",
  /** Empty means this console holds no routing map for the area. */
  routeGraphUrl: params.has("nomap")
    ? ""
    : (import.meta.env.VITE_ROUTE_GRAPH_URL || devRouteGraph),
  /** The static place index the address field searches, read by byte range. */
  /**
   * Which dataset the route graph is a build of — `quebec/car`. Empty
   * keeps the epoch as the graph's content hash. See the note in
   * consumer-web's config: every party on a run must agree on this, and a
   * console that mints tickets is the party that decides it.
   */
  datasetId: import.meta.env.VITE_DATASET_ID || "",
  searchBaseUrl: import.meta.env.VITE_SEARCH_BASE ?? "https://osm.rangefind.dev",
  /** §9.2 — what the graph is, so an estimate can claim a profile match. */
  routeProfile: import.meta.env.VITE_ROUTE_PROFILE || "car",
  /**
   * What to call the area the routing graph covers, when refusing an address
   * outside it. "Outside the map" invites the reply "which map?", and the
   * answer is usually that the wrong region is loaded rather than that the
   * address is wrong. Empty is honest — the sentence reads without it.
   */
  regionName: import.meta.env.VITE_REGION_NAME || "",
  /** Where a customer's link points. The consumer app, wherever it is served. */
  trackBaseUrl: import.meta.env.VITE_TRACK_BASE || devTrackBase || `${browserLocation.origin}/`,
  /**
   * The fleet's own keeper, as a build-time default.
   *
   * A seed enrolled on the Seed screen still wins. That screen is rung 5
   * — an operator deciding to run their own infrastructure — and a
   * default that silently overrode it would make the ladder lie about
   * what this console is using. This is the same fact arriving by
   * configuration instead of by a pasted card, for a deployment that
   * already knows its own address.
   *
   * It goes into tickets as a location, never as a capability, and never
   * into a job offer: an offer is broadcast to strangers and would
   * publish a small operator's own machine to everyone reading an ad.
   */
  meshBootstrap: String(import.meta.env.VITE_MESH_BOOTSTRAP ?? "")
    .split(",")
    .map((address: string) => address.trim())
    .filter((address: string) => address.length > 0),
  /**
   * Whether the console offers the evaluation affordances — today, the
   * one that stands in for a driver's phone.
   *
   * **Off unless asked for**, and that is a production decision rather
   * than tidiness. Standing in mints a device keypair *in this tab*, so
   * a job sealed to it opens in a browser and on no phone at all: an
   * operator who reaches for it on a real evening seals a real
   * customer's delivery to a driver who does not exist, and nothing
   * downstream can tell them, because as far as the protocol is
   * concerned that enrolment is perfectly valid.
   *
   * `?evaluate=1` brings it back for the demonstration the README
   * describes — walking the enrolment gate without a second device in
   * the room.
   */
  evaluation: params.has("evaluate")
} as const;

function parseDayOverride(value: string | null): number | null {
  if (!value) return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})$/u.exec(value.trim());
  if (!match) return null;
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]), 12, 0, 0, 0);
  return Number.isFinite(date.getTime()) ? date.getTime() : null;
}

const overrideMillis = parseDayOverride(params.get("today"));
const realMidday = new Date();
realMidday.setHours(12, 0, 0, 0);

/**
 * Whole days between the real clock and the one this console is using.
 * Zero when nobody asked, which is every real deployment.
 */
export const clockShiftMillis = overrideMillis == null
  ? 0
  : overrideMillis - realMidday.getTime();

export function nowMillis(): number {
  return Date.now() + clockShiftMillis;
}

export function nowSeconds(): number {
  return Math.floor(nowMillis() / 1000);
}

/** The console's own civil date, which is what a service day is named for. */
export function today(): { year: number; month: number; day: number } {
  const date = new Date(nowMillis());
  return { year: date.getFullYear(), month: date.getMonth() + 1, day: date.getDate() };
}
